import { Injectable, inject, signal } from '@angular/core';
import { AuthenticationService } from '@/apiConnection/authentication.service';
import { ConnectivityService } from '@/offline/connectivity.service';
import { DocumentDeviceService } from '@/offline/document-device.service';
import { EchoService } from '@/realtime/echo.service';

const STATUS_CACHE_KEY = 'documentDeviceStatusCache';
/** A heartbeat is considered stale after 3 missed beats (20s interval) - some slack for websocket/subscription jitter across browsers, more than Tablet::online's 2-beat window since this is a coarser business alert, not a live presence dot. */
const STALE_THRESHOLD_MS = 60000;
/** How often to re-evaluate staleness against the wall clock, as requested - the heartbeat itself is received live over the websocket, this only paces when a *stale* status re-triggers the alert. */
const RECHECK_INTERVAL_MS = 60 * 60 * 1000;

export interface DocumentDeviceStatus {
    name: string;
    lastSeenAt: number;
}

/**
 * Passively listens for the offline document-device heartbeat broadcast (public
 * websocket channel scoped by company id, see erp-backend's DocumentDeviceHeartbeat
 * event / EchoService.documentDeviceChannel) and tracks whether a designated device has
 * been heard from recently. Nothing here is backed by the database - `status` is built
 * entirely from what this browser has itself received over the channel, cached to
 * localStorage only so a page reload doesn't forget it instantly (best-effort; a fresh
 * browser session simply waits up to ~20s for the next heartbeat to arrive).
 */
@Injectable({ providedIn: 'root' })
export class DocumentDeviceMonitorService {

    private authenticationService = inject(AuthenticationService);
    private connectivity = inject(ConnectivityService);
    private deviceService = inject(DocumentDeviceService);
    private echoService = inject(EchoService);

    /** Hydrated synchronously from the last heartbeat this browser saw, so it's usable immediately even before a fresh one arrives. */
    status = signal<DocumentDeviceStatus | null>(this.readCachedStatus());

    /** Ticks hourly so computed()s reading it re-evaluate `status`'s staleness against the current wall clock even when no new heartbeat arrives - see showAlert() usage in DocumentDeviceAlertComponent. */
    lastCheckedAt = signal(Date.now());

    private subscribedCompanyId: number | null = null;

    constructor() {
        this.ensureSubscribed();
        setInterval(() => {
            this.ensureSubscribed();
            this.lastCheckedAt.set(Date.now());
        }, RECHECK_INTERVAL_MS);
    }

    /**
     * Whether this browser is allowed to create a document/ticket/purchase right now. Purely local - there's no server record to cross-check against, by design (see DocumentDeviceService).
     * "Online" means the backend actually answers (ConnectivityService), not just `navigator.onLine`: with wifi up but the API unreachable, ApiService still queues the save via its status-0 fallback, so a `navigator.onLine`-only check would let any computer generate offline.
     */
    canGenerateOffline(): boolean {
        return (navigator.onLine && this.connectivity.backendOnline()) || !!this.deviceService.readIdentity();
    }

    isStale(status: DocumentDeviceStatus | null, now: number = Date.now()): boolean {
        return !status || (now - status.lastSeenAt) > STALE_THRESHOLD_MS;
    }

    /** Subscribes to this company's channel once its id is known - a superadmin may not have picked one yet at construction time, so this is retried on every hourly tick too. */
    private ensureSubscribed(): void {
        const companyId = this.authenticationService.effectiveCompanyId;
        if (!companyId || companyId === this.subscribedCompanyId) return;

        this.subscribedCompanyId = companyId;
        this.echoService.documentDeviceChannel(companyId).listen('.device.heartbeat', (data: { name: string; at: string }) => {
            const status: DocumentDeviceStatus = { name: data.name, lastSeenAt: Date.now() };
            this.status.set(status);
            try {
                localStorage.setItem(STATUS_CACHE_KEY, JSON.stringify(status));
            } catch {
                // Ignore (private-browsing storage restrictions etc.) - status still lives in the signal for this session.
            }
        });
    }

    private readCachedStatus(): DocumentDeviceStatus | null {
        try {
            const stored = localStorage.getItem(STATUS_CACHE_KEY);
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    }
}
